import { DownloadOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import { Trans, useTranslation } from 'react-i18next';

import { type ImportResult } from '../../types';
import { ErrorText, SuccessText } from '../common.style';

type ErrorStateProps = { importResult: ImportResult | undefined; importedFileName: string };

export const ErrorState: React.FunctionComponent<ErrorStateProps> = ({
  importResult,
  importedFileName,
}) => {
  const { t } = useTranslation();
  const {
    createdDamageCount,
    duplicateDamageCount,
    duplicateDamageCsv,
    invalidDamageCount,
    invalidDamageCsv,
  } = importResult ?? {};

  return (
    <>
      <SuccessText>
        {t('inspection.damagesSuccessfullyCreated', {
          count: createdDamageCount ?? 0,
          defaultValue_one: '{{count}} damage successfully created.',
          defaultValue_other: '{{count}} damages successfully created.',
        })}
      </SuccessText>
      {!!duplicateDamageCount && duplicateDamageCsv && (
        <>
          <ErrorText $fontWeight="bold" $mb={2}>
            {t('inspection.rowsContainedDuplicateData', {
              count: duplicateDamageCount,
              filename: importedFileName,
              defaultValue_one:
                '{{count}} row in {{filename}} contained duplicate damage information.',
              defaultValue_other:
                '{{count}} rows in {{filename}} contained duplicate damage information.',
            })}
          </ErrorText>
          <ErrorText>
            {t(
              'inspection.duplicatesFileInstructions',
              'Please use the button below to download the duplicates file. Duplicates may not be reuploaded.'
            )}
          </ErrorText>
          <Button icon={<DownloadOutlined />} href={duplicateDamageCsv} target="_blank">
            {t('inspection.downloadDuplicateFile', 'Download Duplicate File')}
          </Button>
        </>
      )}
      {!!invalidDamageCount && invalidDamageCsv && (
        <>
          <ErrorText $fontWeight="bold" $mb={2}>
            {t('assets.rowsContainedInvalidData', {
              count: invalidDamageCount,
              filename: importedFileName,
              defaultValue_one:
                '{{count}} row in {{filename}} contained invalid or incomplete data.',
              defaultValue_other:
                '{{count}} rows in {{filename}} contained invalid or incomplete data.',
            })}
          </ErrorText>
          <ErrorText>
            <Trans
              i18nKey="inspection.invalidDamageErrorFileInstructions"
              t={t}
              values={{ format: '[X1,Y1],[X2,Y2],[X3,Y3],[X4,Y4]' }}
              components={{ b: <b /> }}
              defaults="Please use the button below to download the error file. Complete the missing data and upload again. The following fields are required: <b>Photo File Name and Coordinates</b>. Coordinates must be in the format <b>{{format}}</b>."
            />
          </ErrorText>
          <Button icon={<DownloadOutlined />} href={invalidDamageCsv} target="_blank">
            {t('assets.downloadErrorFile', 'Download Error File')}
          </Button>
        </>
      )}
    </>
  );
};
